import { useCallback } from 'react'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useLocale } from '../../contexts/LocaleContext'
import { SECTION_ICONS } from '../../lib/section-icons'
import { PageHeader } from '../shared/PageHeader'
import { AssetBenchmarkView } from './AssetBenchmarkView'
import { PortfolioBenchmarkView } from './PortfolioBenchmarkView'
import { DEFAULT_METRIC } from './benchmark-utils'

/**
 * Benchmark — costs compared across the portfolio (spec 2026-09-13-01).
 *
 * Three objects, not one chart: the portfolio scatter says where to start, the
 * building strips say where a building stands, the waterfall says why. A value
 * without its `n`, its denominator and the contracts behind it is a rumour, so
 * each object carries them.
 */
export function BenchmarkPage() {
    const { org } = useParams({ from: '/$org/benchmark' })
    const search = useSearch({ from: '/$org/benchmark' })
    const navigate = useNavigate({ from: '/$org/benchmark' })
    const { t } = useTranslation('benchmark')
    const { locale } = useLocale()

    const view = search.view === 'asset' && search.asset ? 'asset' : 'portfolio'
    const assetQuery = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/assets/{assetUid}',
        { params: { path: { orgCode: org, assetUid: search.asset ?? '' } } },
        { enabled: view === 'asset' },
    )
    const assetName = (assetQuery.data as { name?: string } | undefined)?.name

    useBreadcrumbs(
        view === 'asset'
            ? [{ title: t('title'), path: `/${org}/benchmark` }, { title: assetName ?? '…' }]
            : [{ title: t('title') }],
    )

    const setSearch = useCallback(
        (next: Record<string, string | undefined>) => {
            navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, ...next }) as never, replace: true })
        },
        [navigate],
    )

    const toggle = (
        <div className="flex items-center rounded-md border p-0.5" role="group" data-testid="benchmark-view-toggle">
            <button
                type="button"
                onClick={() => setSearch({ view: undefined })}
                aria-pressed={view === 'portfolio'}
                className={`rounded px-3 py-1 text-sm ${view === 'portfolio' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="benchmark-view-portfolio"
            >
                {t('view.portfolio')}
            </button>
            <button
                type="button"
                disabled={!search.asset}
                onClick={() => setSearch({ view: 'asset' })}
                aria-pressed={view === 'asset'}
                title={!search.asset ? t('pick_building') : undefined}
                className={`rounded px-3 py-1 text-sm disabled:opacity-50 ${view === 'asset' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                data-testid="benchmark-view-asset"
            >
                {t('view.asset')}
            </button>
        </div>
    )

    return (
        <div className="space-y-4">
            <PageHeader
                icon={SECTION_ICONS.benchmark}
                title={view === 'asset' && assetName ? `${t('title')} — ${assetName}` : t('title')}
                description={t('description')}
                actions={toggle}
                data-testid="benchmark-title"
            />
            {view === 'asset' && search.asset ? (
                <AssetBenchmarkView org={org} assetUid={search.asset} windowEnd={search.window_end} locale={locale} />
            ) : (
                <PortfolioBenchmarkView
                    org={org}
                    metric={search.metric ?? DEFAULT_METRIC}
                    breakdownKey={search.key}
                    windowEnd={search.window_end}
                    strata={search.strata}
                    currency={search.currency}
                    locale={locale}
                    onChange={setSearch}
                    onOpenBuilding={(uid) => navigate({ search: (prev: Record<string, unknown>) => ({ ...prev, view: 'asset', asset: uid }) as never })}
                />
            )}
        </div>
    )
}
